import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, FormLabel, Input, Select,
  Alert, AlertIcon, AlertTitle, AlertDescription, Checkbox,
  Button, Text, Switch } from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import AddressFormatter from '@shopify/address';

const AddressForm = ({ address, onSave, onDelete, namePrefixes, isDefault, onDefaultChange, defaultCountry, isFirstAddress = false }) => {
  const { t, i18n } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [countryFormatter, setCountryFormatter] = useState(null);
  const [addressFormatter, setAddressFormatter] = useState(null);
  const [error, setError] = useState(null);

  const extraData = address.address.data ? JSON.parse(address.address.data) : null;
  const defaultValues = address ? {
    defaultAddress: isFirstAddress ? true : (address.default_address || false),
    addressTypes: address.address_types || '',
    careOf: extraData?.care_of || '',
    namePrefix: extraData?.name_prefix || '',
    firstName: address.address.first_name || '',
    lastName: address.address.last_name || '',
    company: address.address.organisation_name || '',
    address1: address.address.thoroughfare || '',
    address2: address.address.premise || '',
    city: address.address.locality || '',
    province: address.address.administrative_area || '',
    zip: address.address.postal_code || '',
    country: address.address.country || defaultCountry || '',
  } : {
    defaultAddress: false,
    addressTypes: '',
    namePrefix: '',
    firstName: '',
    careOf: '',
    lastName: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    province: '',
    zip: '',
    country: '',
  };

  const { control,
    handleSubmit,
    setValue,
    watch } = useForm({
    defaultValues: defaultValues,
  });

  const selectedCountry = watch('country');

  useEffect(() => {
    const initializeAddressFormatter = async () => {
      try {
        const formatter = new AddressFormatter(i18n.language);
        setAddressFormatter(formatter);

        const loadedCountries = await formatter.getCountries();
        setCountries(loadedCountries);

        if (defaultCountry && !selectedCountry) {
          setValue('country', defaultCountry);
        }

      } catch (error) {
        console.error('Error initializing AddressFormatter:', error);
        setError('An error occurred while initializing the address formatter. Please try again.');
      }
    };

    initializeAddressFormatter();
  }, [i18n.language, defaultCountry, selectedCountry, setValue]);

  useEffect(() => {
    const loadProvinces = async () => {
      if (selectedCountry && addressFormatter) {
        try {
          const countryFormatter = await addressFormatter.getCountry(selectedCountry);
          setProvinces(countryFormatter.zones);
          setCountryFormatter(countryFormatter); // Update province label based on country
          
        } catch (error) {
          console.error('Error loading provinces:', error);
          setError('An error occurred while loading the provinces. Please try again.');
        }
      }
    };

    loadProvinces();
  }, [selectedCountry, addressFormatter]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === 'defaultAddress') {
        onDefaultChange(value[name]);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, onDefaultChange]);

  const onSubmit = async (data) => {
    try {
      onSave(data);
    } catch (error) {
      setError('An error occurred while saving. Please try again.');
    }
  };

  const renderField = useCallback((field) => {
    // Only render the 'country' field if no country is selected
    if (!countryFormatter && field !== 'country') {
      return null;
    }

    switch (field) {
      case 'defaultAddress':
        return (
          <Controller
            name="defaultAddress"
            control={control}
            render={({ field }) => (
              <FormControl marginBottom="10px" className="default-address">
                <FormLabel>{t('addressForm.defaultAddress')}</FormLabel>
                <Switch
                  id="isDefault"
                  isChecked={isFirstAddress || isDefault}
                  disabled={isFirstAddress}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    setValue('defaultAddress', e.target.checked);
                    onDefaultChange(e.target.checked);
                  }}
                />
                {isFirstAddress && (
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    {t('addressForm.firstAddressDefault')}
                  </Text>
                )}
              </FormControl>
            )}
          />
        );
      case 'addressTypes':
        return (
          <Controller
            name="addressTypes"
            control={control}
            render={({ field }) => {
              const addressTypes = [
                { value: 'letters', label: 'Letters' },
                { value: 'consignor_statement', label: 'Consignor address' },
                { value: 'shipping_for_lots', label: 'Shipping for lots' },
                { value: 'catalogue', label: 'Catalogue' },
                { value: 'invoices', label: 'Invoices' },
              ];
              return (
                <FormControl marginBottom="10px" className="address-type">
                  <Box display="flex" flexWrap="wrap" gap="10px">
                    <FormLabel>{t('addressForm.addressType')}</FormLabel>
                    {addressTypes.map((type) => (
                      <Checkbox
                        key={type.value}
                        value={type.value}
                        isChecked={field.value.includes(type.value)}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const selectedTypes = isChecked
                            ? [...field.value, type.value]
                            : field.value.filter((selectedType) => selectedType !== type.value);
                          field.onChange(selectedTypes);
                          setValue('addressTypes', selectedTypes);
                        }}
                      >
                        {t(`addressForm.addressTypes.${type.value}`)}
                      </Checkbox>
                    ))}
                  </Box>
                </FormControl>
              );
            }}
          />
        );
      case 'namePrefix':
        return (
          <Controller
            key={field}
            name="namePrefix"
            control={control}
            render={({ field }) => (
              <FormControl marginBottom="10px" className="title">
                <FormLabel>{t('addressForm.title')}</FormLabel>
                <Select
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue('namePrefix', e.target.value);
                  }}
                >
                  <option value="">{t('addressForm.selectTitle')}</option>
                  {namePrefixes.map((prefix) => (
                    <option key={prefix.id} value={prefix.id}>
                      {prefix.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        );
      case 'firstName':
        return (
          <Controller
            key={field}
            name="firstName"
            control={control}
            render={({ field }) => (
              <FormControl marginBottom="10px" className="first-name">
               <FormLabel>{t('addressForm.firstName')}</FormLabel>
                <Input {...field} type="text" />
              </FormControl>
            )}
          />
        );
      case 'lastName':
        return (
          <Controller
            key={field}
            name="lastName"
            control={control}
            render={({ field }) => (
              <FormControl marginBottom="10px" className="last-name">
                <FormLabel>{t('addressForm.lastName')}</FormLabel>
                <Input {...field} type="text" />
              </FormControl>
            )}
          />
        );
      case 'careOf':
        return (
          <Controller
            key={field}
            name="careOf"
            control={control}
            render={({ field }) => (
              <FormControl marginBottom="10px" className="care-of">
                <FormLabel>{t('addressForm.careOf')}</FormLabel>
                <Input {...field} type="text" />
              </FormControl>
            )}
          />
        );
      case 'address1':
        return (
          <Controller
            key={field}
            name="address1"
            control={control}
            render={({ field }) => (
              <FormControl marginBottom="10px" className="address1" isRequired>
                <FormLabel>{countryFormatter.labels.address1}</FormLabel>
                <Input {...field} type="text" required/>
              </FormControl>
            )}
          />
        );
      case 'address2':
        return (
          <Controller
            key={field}
            name="address2"
            control={control}
            render={({ field }) => (
              <FormControl marginBottom="10px" className="address2">
                <FormLabel>{countryFormatter.labels.address2}</FormLabel>
                <Input {...field} type="text" />
              </FormControl>
            )}
          />
        );
      case 'city':
        return (
          <Controller
            key={field}
            name="city"
            control={control}
            render={({ field }) => (
              <FormControl marginBottom="10px" className="city" isRequired>
                <FormLabel>{countryFormatter.labels.city}</FormLabel>
                <Input {...field} type="text" required/>
              </FormControl>
            )}
          />
        );
      case 'zip':
        return (
          <Controller
            key={field}
            name="zip"
            control={control}
            render={({ field }) => (
              <FormControl marginBottom="10px" className="zip" isRequired>
                <FormLabel>{countryFormatter.labels.postalCode}</FormLabel>
                <Input {...field} type="text" required/>
              </FormControl>
            )}
          />
        );
      case 'province':
        return (countryFormatter.zones && countryFormatter.zones.length > 0 && (
          <Controller
            key={field}
            name="province"
            control={control}
            render={({ field }) => (
              <FormControl marginBottom="10px" className="province" isRequired>
                <FormLabel>{countryFormatter.labels.zone}</FormLabel>
                <Select {...field} required>
                  <option value="">Select {countryFormatter.labels.zone.toLowerCase()}</option>
                  {provinces.map((province) => (
                    <option key={province.code} value={province.code}>
                      {province.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        ));
      case 'country':
        return (
          <Controller
            key={field}
            name="country"
            control={control}
            render={({ field }) => (
               
              <FormControl marginBottom="10px" className="country">
                <FormLabel>{t('addressForm.country')}</FormLabel>
                <Select
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue('country', e.target.value);
                  }}
                >
                  <option value="">{t('addressForm.selectCountry')}</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        );
      default:
        return null;
    }
  },
    [control, provinces, countries, setValue, countryFormatter, namePrefixes, isDefault, onDefaultChange, t]);

  return (
    <Box marginBottom="20px" borderWidth="1px" borderRadius="lg">
      {error && (
        <Alert status="error" marginBottom="20px">
          <AlertIcon />
          <AlertTitle>{t('addressForm.error')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        
        {renderField('defaultAddress')}
        {renderField('addressTypes')}

        {countryFormatter && (
          <Box className="address-override" marginBottom="20px" borderWidth="1px" borderRadius="lg" padding="10px">
            <Text color="gray.500" fontSize="sm" marginBottom="20px">
              {t('addressForm.override_address_name')}
            </Text>
            {renderField('namePrefix')}
            {renderField('firstName')}
            {renderField('lastName')}
          </Box>
        )}

        {renderField('careOf')}
        {renderField('address1')}
        {renderField('address2')}
        {renderField('city')}
        {renderField('province')}
        {renderField('zip')}
        {renderField('country')}

        {/* Render extra data fields */}
        {address.extraData && address.extraData.keys.length > 0 && (
          <Box marginTop="20px">
            <h4>Extra Fields</h4>
            <ul>
              {address.extraData.keys.map((key) => (
                <li key={key}>
                  {key}: <b>{address.extraData.data[key]}</b>
                </li>
              ))}
            </ul>
          </Box>
        )}

        <Box marginTop="20px">
          {onDelete && (
            <Button type="button" className="delete" colorScheme="red" onClick={() => onDelete(address)}>
              {t('addressForm.delete')}
            </Button>
          )}
          <Button type="submit" className="submit" colorScheme="blue" ml="4">
            {t('addressForm.save')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AddressForm;